import { Icon } from '@iconify/react';
import axios from 'axios';
import Button from 'components/Button/Button';
import { Form } from 'components/Form/Form';
import { Input } from 'components/Form/Input';
import { useToast } from 'hooks/useToast';
import Image from 'next/image';
import { Dialog } from 'primereact/dialog';
import { useEffect, useState } from 'react';
import { MODAL_ATTRIBUTES } from 'utils/constants';
import { RULES } from 'utils/constants';
import { printError } from 'utils/helpers';

import contact from '../../../assets/icons/contact.svg';
import cn from './ContactButton.module.sass';

type Props = {};

function ContactButton({}: Props) {
  const dialogOptions = [
    {
      label: 'Indienen belverzoek',
      icon: 'ph:phone-call',
      callback: () => setDialogScreen('CALL')
    },
    {
      label: 'Vul contactformulier in',
      icon: 'material-symbols:contact-mail-outline',
      callback: () => setDialogScreen('FORM')
    },
    {
      label: 'Email ons',
      icon: 'ph:envelope-simple',
      link: 'mailto:info@cetrainer.nl?Subject=Bericht van CETrainer.nl'
    }
  ];

  const { toast } = useToast();

  const [scrollPos, setScrollPos] = useState(window.scrollY);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [dialogScreen, setDialogScreen] = useState<string>('INITIAL');

  useEffect(() => {
    if (dialogVisible) setDialogScreen('INITIAL');
  }, [dialogVisible]);

  function handleScroll() {
    setScrollPos(window.scrollY);
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  async function handleContactSubmit(data: any) {
    const url = 'https://formspree.io/f/mzbogpor';

    try {
      await axios.post(url, {
        ...data,
        email: data.email.trim(),
        name: data.name.trim(),
        organization: data.organization.trim(),
        description: data.description.trim()
      });

      setDialogVisible(false);

      // TODO: Translate
      toast.current?.show({
        severity: 'success',
        summary: 'Message sent',
        detail: 'We will get back to you as soon as possible',
        life: 3000
      });
    } catch (e) {
      printError(e);
    }
  }

  async function handleCallSubmit(data: any) {
    const url = 'https://formspree.io/f/myyojgoe';

    try {
      await axios.post(url, {
        ...data,
        name: data.name.trim(),
        phone: data.phone.trim(),
        description: data.description.trim(),
        time: data.time.trim()
      });

      setDialogVisible(false);

      // TODO: Translate
      toast.current?.show({
        severity: 'success',
        summary: 'Call request sent',
        detail: 'We will get back to you as soon as possible',
        life: 3000
      });
    } catch (e) {
      printError(e);
    }
  }

  return (
    <>
      <Dialog
        header="Neem contact op met CETrainer"
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        className={cn.dialog__wrapper}
        {...MODAL_ATTRIBUTES}
      >
        {/* Initial Screen */}
        {dialogScreen === 'INITIAL' && (
          <div className={cn.dialog}>
            {dialogOptions.map((ele, i) => (
              <div className={cn.dialog__option} key={i}>
                {ele.link ? (
                  <a href={ele.link} className={cn.dialog__link}>
                    <Icon icon={ele.icon} className={cn.dialog__icon} />
                    {ele.label}
                  </a>
                ) : (
                  <button onClick={ele.callback}>
                    <span className={cn.dialog__option__label}>
                      <Icon icon={ele.icon} className={cn.dialog__icon} />
                      {ele.label}{' '}
                      <Icon
                        icon="ph:arrow-right"
                        style={{ marginLeft: 'auto' }}
                      />
                    </span>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Contact Screen */}
        {dialogScreen === 'FORM' && (
          <Form onSubmit={handleContactSubmit} className={cn.form__contact}>
            <Input title="Naam" name="name" rules={{ required: true }} />

            <Input
              title="Email"
              name="email"
              rules={{ required: true, pattern: RULES.EMAIL.pattern }}
            />

            <Input title="Organisatie" name="organization" />

            <Input
              title="Toelichting"
              name="description"
              rules={{ required: true }}
            />

            <div className={cn.form__contact__buttons}>
              <Button
                onClick={() => setDialogScreen('INITIAL')}
                bgType="orange"
              >
                <Icon icon="ph:arrow-left" />
                <p>Terug</p>
              </Button>

              <Button type="submit">
                <Icon icon="ph:paper-plane" />
                <p>Stuur formulier op</p>
              </Button>
            </div>
          </Form>
        )}

        {/* Call Screen */}
        {dialogScreen === 'CALL' && (
          <Form onSubmit={handleCallSubmit} className={cn.form__call}>
            <Input title="Naam" name="name" rules={{ required: true }} />

            <Input
              title="Telefoonnummer"
              name="phone"
              rules={{ required: true }}
            />

            <Input
              title="Korte toelichting"
              name="description"
              rules={{ required: true }}
            />

            <Input title="Geschikt tijdstip" name="time" />

            <div className={cn.form__contact__buttons}>
              <Button
                onClick={() => setDialogScreen('INITIAL')}
                bgType="orange"
              >
                <Icon icon="ph:arrow-left" />
                <p>Terug</p>
              </Button>

              <Button type="submit">
                <Icon icon="ph:paper-plane" />
                <p>Dien je belverzoek in</p>
              </Button>
            </div>
          </Form>
        )}
      </Dialog>
      <Button
        className={`fixed bottom-[-8rem] right-[2rem] rounded-[100px] z-[49] transition-all duration-300 ease-in w-[60px] h-[60px] lg:w-[80px] lg:h-[80px] ${
          scrollPos > 20 && 'bottom-[2rem]'
        }`}
        onClick={() => setDialogVisible(true)}
        bgType='orange'
      >
        <Image
          src={contact}
          alt="contact"
          width={40}
          height={40}
          className="w-[30px] h-[30px] lg:w-[40px] lg:h-[40px]"
        />
      </Button>
    </>
  );
}

export default ContactButton;
